import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, MapPin, Shield, Phone, CalendarCheck } from "lucide-react";

const RULES = [
  "You meet the minimum age to play for money in your state — 18+, and 21+ in Arizona, Louisiana and Nevada.",
  "You will only enter paid contests from a US state where paid fantasy contests are legal.",
  "You are playing at your own risk. PickemSports is a game of skill with no guaranteed returns.",
  "You will not enter contests if you or your household are self-excluded from gambling programs.",
  "You accept that entries are non-refundable once submitted and paid.",
  "You will comply with your state's tax reporting requirements for any winnings.",
  "You understand that repeated or excessive play may indicate problem gambling — help is 1-800-GAMBLER.",
];

const ageFromDob = (dobStr) => {
  if (!dobStr) return null;
  const dob = new Date(`${dobStr}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

export default function TermsModal({ open, onClose, forceOpen = false }) {
  const { user, refreshUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState("");
  const [dob, setDob] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/geo/check");
        setGeo(data);
      } catch (err) { console.warn("Geo check failed", err); }
    })();
    setSignature(user?.name || "");
    setDob(user?.date_of_birth || "");
  }, [open, user?.name, user?.date_of_birth]);

  if (!open) return null;

  const dobLocked = Boolean(user?.date_of_birth);
  const minAge = geo?.min_age || 18;
  const age = ageFromDob(dob);
  const underage = age !== null && age < minAge;

  const submit = async () => {
    if (!dob) return toast.error("Please enter your date of birth");
    if (underage) return toast.error(`You must be ${minAge}+ to play for money in your state`);
    if (!checked) return toast.error("Please check the box to acknowledge the rules");
    if (!signature.trim()) return toast.error("Please type your full name as a signature");
    setSubmitting(true);
    try {
      await api.post("/legal/accept-terms", {
        accepted: true,
        signature: signature.trim(),
        date_of_birth: dobLocked ? undefined : dob,
      });
      toast.success("Thanks! You're cleared to play.");
      await refreshUser();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept terms");
    } finally {
      setSubmitting(false);
    }
  };

  const status = geo?.classification?.status;
  const statusColor = status === "blocked" ? "#FF3B30" : status === "restricted" ? "#FFB300" : status === "legal" ? "#00FF66" : "#71717A";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="terms-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !forceOpen) onClose?.();
      }}
    >
      <div className="w-full max-w-2xl bg-[#0a0a0a] border border-white/10 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-2 text-[#00FF66] text-xs uppercase tracking-widest mb-1">
            <Shield size={12} /> Age &amp; Player Agreement
          </div>
          <h2 className="font-display text-3xl">VERIFY AGE · RULES OF PLAY</h2>
          <p className="text-white/50 text-sm mt-2">
            Before your first paid contest we need your date of birth and your signature. Free practice contests
            never require this.
          </p>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {geo && (
            <div
              className="border p-3 flex items-center gap-3"
              style={{ borderColor: `${statusColor}66`, background: `${statusColor}0f` }}
            >
              <div
                className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
                style={{ background: `${statusColor}30`, color: statusColor }}
              >
                <MapPin size={16} />
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-semibold">
                  Detected location: {geo.state_name || "Unknown"}
                  {geo.state && <span className="text-white/40 ml-1">· {geo.state}</span>}
                </div>
                <div className="text-white/60 text-xs">{geo.classification?.reason}</div>
              </div>
              <span
                className="text-xs uppercase tracking-widest px-2 py-1 border rounded-sm"
                style={{ color: statusColor, borderColor: `${statusColor}66` }}
              >
                {status || "unknown"}
              </span>
            </div>
          )}

          {/* Date of birth — write once */}
          <div className="border border-white/10 bg-[#141414] p-4">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <label className="text-white/40 text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
                <CalendarCheck size={12} /> Date of birth
              </label>
              <span className="text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-2 py-0.5 rounded-sm">
                Minimum age here: {minAge}+
              </span>
            </div>
            <input
              type="date"
              value={dob}
              max={today}
              disabled={dobLocked}
              onChange={(e) => setDob(e.target.value)}
              data-testid="terms-dob-input"
              className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] disabled:opacity-60 rounded-sm px-3 py-2.5 text-base outline-none"
            />
            {dobLocked ? (
              <div className="text-[#00FF66] text-xs mt-2" data-testid="terms-dob-locked">
                On file — contact support if this needs correcting.
              </div>
            ) : (
              <div className="text-white/40 text-xs mt-2">
                Recorded once and used to enforce your state&apos;s minimum age. It cannot be changed later.
              </div>
            )}
            {underage && (
              <div
                className="mt-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-xs p-2"
                data-testid="terms-underage-warning"
              >
                You&apos;re {age} — paid contests require {minAge}+ in {geo?.state_name || "your state"}. Free practice
                contests are still open to you.
              </div>
            )}
          </div>

          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest mb-2">You agree that:</div>
            <ul className="space-y-2 text-sm text-white/80">
              {RULES.map((r, i) => (
                <li key={r} className="flex items-start gap-2">
                  <span className="text-[#00FF66] font-display text-sm flex-shrink-0 w-5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 text-xs text-white/70 flex items-start gap-2">
            <AlertTriangle size={14} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
            <div>
              Contests carry financial risk. Never wager more than you can afford to lose. If you feel play is
              becoming a problem, call{" "}
              <a href="tel:18004262537" className="text-[#FF3B30] underline inline-flex items-center gap-1">
                <Phone size={11} /> 1-800-GAMBLER
              </a>{" "}
              — free, confidential, 24/7.
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="terms-checkbox"
              className="mt-1 accent-[#00FF66]"
            />
            <span className="text-white/80">
              I have read and agree to the rules above. The date of birth I entered is accurate and I am playing
              from a legal jurisdiction.
            </span>
          </label>

          <div>
            <label className="block text-white/40 text-xs uppercase tracking-widest mb-1">
              Sign with your full legal name
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Full name"
              data-testid="terms-signature"
              className="w-full bg-[#141414] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 text-lg font-display tracking-wide outline-none"
              style={{ fontStyle: "italic" }}
            />
          </div>
        </div>

        <div className="p-4 border-t border-white/10 flex items-center justify-between gap-3">
          {!forceOpen && (
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white text-sm uppercase tracking-widest font-semibold px-3 py-2"
              data-testid="terms-cancel"
            >
              Not yet
            </button>
          )}
          <button
            onClick={submit}
            disabled={submitting || !checked || !dob || underage || !signature.trim() || status === "blocked"}
            data-testid="terms-accept-btn"
            className="ml-auto bg-[#00FF66] hover:bg-[#00e65b] disabled:bg-white/10 disabled:text-white/30 text-black font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2"
          >
            {submitting ? "SIGNING..." : "Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
